import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import OfferBNPLAd from '../OfferBNPLAd'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ShareButton from 'components/Common/ShareWithFriendsButton'
import FlashOfferSummary from './FlashOfferSummary'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import { useAppSelector } from 'hooks/reduxHooks'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import {
  getCheapestSelectedPackageOptionWithDefault,
  isAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import { shouldShowSalesUSP } from 'selectors/featuresSelectors'
import FlashHotelPropertyDetails from './FlashHotelPropertyDetails'
import ContactUSP from '../ContactUSP'
import FlashHotelUSPs from './FlashHotelUSPs'
import Divider from 'LuxKit/Divider'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Group from 'components/utils/Group'
import OfferPageLabelGroup from '../OfferPageLabelGroup'
import OfferDescription from '../OfferDescription'
import OfferPageIntroInclusions from '../OfferPageIntroInclusions'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import FlashHotelOfferDescription from './FlashHotelMobileVariant/FlashHotelOfferDescription'
import FlashHotelInclusionsAndHighlightsModal from './FlashHotelMobileVariant/Modals/FlashHotelInclusionsAndHighlightsModal'
import FlashOfferIntroPricingBlock from './FlashHotelMobileVariant/FlashOfferIntroPricingBlock'
import config from 'constants/config'
import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'
import FlashOfferWithFlightsSummary from './FlashOfferWithFlightsSummary'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import TextButton from 'LuxKit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import { getCanAgentExportMedia } from 'agentHub/selectors/agentHubSelectors'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import CharterBundleUSPs from '../CharterBundle/CharterBundleUSPs'
import { SiteTakeoverCampaignUSP } from 'takeovers/utils/components/SiteTakeoverCampaignUSP'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { useActiveInclusionFilters } from 'hooks/useActiveInclusionFilters'
import { reorderTileInclusionsByActiveFilters } from 'lib/offer/inclusionUtils'
import ContentTagLabels from 'components/Content/ContentTagLabels'

const OfferIntroGrid = styled(LayoutContainer)`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};
  padding-top: ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(28)};
    padding-bottom: ${rem(28)};
    grid-template-columns: 1fr min-content;
    grid-gap: ${rem(16)} ${rem(48)};
  }
`

const SummaryColumn = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    flex-direction: column;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const SummaryBlock = styled.div`
  ${mediaQueryUp.desktop} {
    position: sticky;
    top: ${rem(12)};
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(FlashOfferSummary)`
  min-width: ${rem(308)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(382)};
  }
`

const HotelFlightsSummary = styled(FlashOfferWithFlightsSummary)`
  min-width: ${rem(382)};
`

const CharterCallout = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
`

const CharterCalloutText = styled(Group)`
  flex: 1;
  min-width: ${rem(160)};
`

interface Props {
  cheapestSelectedOption?: App.OfferPackageOptionView
  cheapestOption: App.OfferPackageOptionView
  checkIn?: string
  offer: App.HotelOffer
  className?: string
  onViewPackageOptions: () => void
  flightDestination?: App.Airport
  shouldShowSalesPhoneUSP: boolean
  shareModalConfig: ShareConfig
  flightOrigin?: App.Airport
  availableForPurchase?: boolean
  rooms?: Array<App.RoomOccupants>
  charterBundleRoute?: App.CharterBundleRoute
}

function FlashHotelIntro(props: Props) {
  const {
    checkIn,
    offer,
    className,
    onViewPackageOptions,
    flightDestination,
    shouldShowSalesPhoneUSP,
    shareModalConfig,
    flightOrigin,
    cheapestOption,
    availableForPurchase,
    rooms,
    charterBundleRoute,
  } = props
  const property = offer.property
  const showModal = useContext(ModalContext)
  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])
  const offerPageDispatch = useOfferPageDispatch()
  const charterRoute = charterBundleRoute ? CHARTER_FLIGHT_DETAILS[charterBundleRoute] : undefined

  const charterContact: App.GeoContact | undefined = useMemo(() => {
    if (!charterRoute) return undefined
    return {
      humanReadable: charterRoute.supportNumber.humanReadable,
      number: charterRoute.supportNumber.humanReadable.replace(/\s+/g, ''),
    }
  }, [charterRoute])

  const hasFlights = !!cheapestOption.flightPrice && !offer.flightCacheDisabled
  const hideNoCacheMessaging = !hasFlights && offer.bundledWithFlightsOnly
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled
  const showHotelPrice = !offer.bundledWithFlightsOnly || (offer.bundledWithFlightsOnly && flightsEnabled && !hideNoCacheMessaging && hasFlights)
  const showHotelPriceWithFlights = !offer.bundledWithFlightsOnly && !!flightsEnabled && !hideNoCacheMessaging && hasFlights && !charterBundleRoute
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isExportMediaEnabled = config.EXPORT_MEDIA_CONTENT_ENABLED
  const isAgentHubEnabled = config.agentHub.isEnabled
  const mediaExportURL = isAgentHubEnabled ? buildAgentHubQuotingURL(offer.id, { currency, region }) : undefined
  const canAgentExportMedia = getCanAgentExportMedia
  const isNewMobileMultipleRoom = (rooms?.length ?? 0) > 1

  const showHotelFlightsV2Summary = availableForPurchase && flightsEnabled && !hideNoCacheMessaging && !offer.bundledWithFlightsOnly && !charterBundleRoute

  const activeInclusionCategories = useActiveInclusionFilters()

  // Boost tile inclusions based on active filters
  const boostedTileInclusions = useMemo(() => {
    if (!offer.tileInclusions?.length) return offer.tileInclusions
    return reorderTileInclusionsByActiveFilters(offer.tileInclusions, activeInclusionCategories)
  }, [offer.tileInclusions, activeInclusionCategories])

  const openHighlightsAndInclusions = useCallback(async() => {
    showModal(
      <FlashHotelInclusionsAndHighlightsModal
        highlightsMarkdown={offer.highlights}
        additionalDescriptionMarkdown={offer.additionalDescription}
        tileInclusions={boostedTileInclusions}
      />,
    )
  }, [offer, showModal, boostedTileInclusions])
  const siteTakeover = useCurrentSiteTakeover({ vertical: 'hotel' })

  const onSelectRoomsClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleMultiRoomSelectRooms, isMultiRoomSelectRooms: true })
    if (!checkIn) {
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'duration' })
    } else {
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'multiRoomSelect' })
    }
  }, [checkIn, offerPageDispatch])

  const handleScheduleCallback = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    showModal(<RequestAgentCallbackModal type="SALES" />)
  }, [showModal])

  const shouldShowToolTip = useScreenSizeUp('tablet')
  return (
    <OfferIntroGrid className={className} data-testid="offer-intro">
      <Group direction="vertical" gap={20}>
        <CSSBreakpoint min="tablet">
          <ContentTagLabels offerId={offer.id} page="offer" />
          <OfferPageLabelGroup
            offer={offer}
            suppressTakeoverCampaignTagWhenUSPVisible
          />
        </CSSBreakpoint>
        <FlashHotelPropertyDetails
          property={property}
          offer={offer}
          suppressTakeoverCampaignTagWhenUSPVisible
          charterBundleRoute={charterBundleRoute}
        />
        {offer.campaignTags.length > 0 && offer.campaignTags.map(campaignTagId => <SiteTakeoverCampaignUSP key={campaignTagId} campaignTagId={campaignTagId} />)}
        <CSSBreakpoint max="tablet">
          <Group direction="vertical" gap={20}>
            {showHotelPrice && <FlashOfferIntroPricingBlock showHotelPriceWithFlights={showHotelPriceWithFlights} />}
            {charterRoute && (
              <>
                <Divider kind="primary" />
                <CharterCallout>
                  <CharterCalloutText direction="vertical" gap={4}>
                    <BodyText variant="medium" weight="bold">
                      Have questions?
                    </BodyText>
                    <TextLink
                      href="#"
                      size="small"
                      underline
                      onClick={handleScheduleCallback}
                    >
                      Schedule a call back
                    </TextLink>
                  </CharterCalloutText>
                  <TextButton
                    kind="secondary"
                    size="medium"
                    startIcon={<LinePhoneIcon />}
                    href={charterRoute.supportNumber.href}
                    rel="nofollow"
                  >
                    Call now
                  </TextButton>
                </CharterCallout>
              </>
            )}
            {isNewMobileMultipleRoom && <SoftOfferPageWalledGarden>
              <TextButton kind="primary" onClick={onSelectRoomsClick}>{checkIn ? 'Select rooms' : 'Check availability'}</TextButton>
            </SoftOfferPageWalledGarden>}
            {boostedTileInclusions && boostedTileInclusions.length > 2 && <>
              <Divider kind="primary" />
              <Group direction="vertical" gap={12}>
                <Heading variant="heading3">Handpicked inclusions</Heading>
                <OfferPageIntroInclusions tileInclusionsList={boostedTileInclusions} />
              </Group>
              <TextLink size="medium" onClick={openHighlightsAndInclusions}>View all highlights & inclusions</TextLink>
            </>}
            {offer.description && <>
              <Divider kind="primary" />
              <FlashHotelOfferDescription descriptionMarkdown={offer.description} facilitiesMarkdown={offer.facilities} />
            </>}
            {charterBundleRoute && <Divider kind="primary" />}
          </Group>
        </CSSBreakpoint>
        <CSSBreakpoint min="desktop">
          <Group direction="vertical" gap={20}>
            {offer.description && <OfferDescription description={offer.description} />}
            <SoftOfferPageWalledGarden>
              {offer.additionalDescription && <>
                <Divider kind="primary" />
                <MarkdownRender fontSize="medium" type="compact" content={offer.additionalDescription} />
              </>}
            </SoftOfferPageWalledGarden>
          </Group>
        </CSSBreakpoint>

        <SoftOfferPageWalledGarden>
          {siteTakeover?.offerPage?.usps}
          {!charterBundleRoute && <>
            <Divider kind="primary" />
            <Group direction="vertical" gap={12}>
              <CSSBreakpoint max="tablet">
                <Heading variant="heading3">Why you should book with us</Heading>
              </CSSBreakpoint>
              <FlashHotelUSPs offer={offer} checkIn={checkIn} onViewPackageOptions={onViewPackageOptions} />
            </Group>
          </>}
          {charterBundleRoute && <CharterBundleUSPs />}
          {shouldShowSalesPhoneUSP && (<>
            {!charterBundleRoute && <Divider kind="primary" />}
            <ContactUSP contactOverride={charterContact} />
          </>)}
        </SoftOfferPageWalledGarden>
      </Group>
      <SummaryColumn>
        <SummaryBlock>
          <CSSBreakpoint min="tablet">
            <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="end">
              <TripGuard inverse>
                <ShareModal shareConfig={shareModalConfig} openOnHash="#share">
                  <ShareButton horizontalOutdent={isExportMediaEnabled ? undefined : 'end'} showToolTip={shouldShowToolTip} />
                </ShareModal>
              </TripGuard>
              {isExportMediaEnabled && canAgentExportMedia && <TripGuard inverse>
                <ExportMediaContentButton href={mediaExportURL} target="_blank" />
              </TripGuard>}
              <TripGuard>
                <ShareModal shareConfig={shareModalConfig} openOnHash="#share">
                  <ShareButton showToolTip={shouldShowToolTip} />
                </ShareModal>
              </TripGuard>
              <TripGuard>
                <BookmarkButton offer={offer} />
              </TripGuard>
              {isExportMediaEnabled && canAgentExportMedia && <TripGuard>
                <ExportMediaContentButton href={mediaExportURL} target="_blank" />
              </TripGuard>}
            </Group>
          </CSSBreakpoint>
          <div>
            {!showHotelFlightsV2Summary &&
              <HotelSummary
                onViewPackageOptions={onViewPackageOptions}
                flightDestination={flightDestination}
              />}
            {showHotelFlightsV2Summary &&
              <HotelFlightsSummary
                onViewPackageOptions={onViewPackageOptions}
                flightDestination={flightDestination}
              />}
          </div>
          <OfferBNPLAd prices={allPrices} />
        </SummaryBlock>
      </SummaryColumn>
    </OfferIntroGrid>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice ?? 0, 1000),
  cheapestOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  checkIn: state.checkInDate,
  offer: state.offer,
  flightOrigin: state.airport,
  availableForPurchase: isAvailableForPurchase(state, appState),
  rooms: state.rooms,
  open: !!state.activeFilter,
  charterBundleRoute: state.charterBundleRoute,
}), FlashHotelIntro, preventRenderWithOpenFilter)
